import { useMemo, memo, useState, useEffect, useRef } from "react";
import { Table, Tooltip } from "antd";
import CustomButton from "../CustomButton/Button";
import useTableColumnPreference from "../../hooks/useTableColumnPreference";
import "./CustomTable.scss";

const getDynamicPageSize = () => {
    if (typeof window === "undefined" || !window.innerHeight) return 10;
    // Overhead: Navbar (70px) + content margins/padding (54px) + TableHeader (67px)
    // + Table Header row (45px) + Pagination & margins (65px) + Safety buffer (70px)
    // Total overhead ~370px
    const availableHeight = window.innerHeight - 370;
    const calculated = Math.floor(availableHeight / 45);
    // Keep reasonable bounds (minimum 5, maximum 30)
    return Math.max(5, Math.min(30, calculated || 10));
};

const renderEllipsisCell = (value) => {
    if (value === null || value === undefined || value === "") {
        return <span className="table-ellipsis-cell">-</span>;
    }

    const displayText = typeof value === "string" ? value : String(value);
    return (
        <Tooltip title={displayText} placement="topLeft">
            <span className="table-ellipsis-cell">{displayText}</span>
        </Tooltip>
    );
};

const applyDefaultEllipsis = (cols = []) =>
    cols.map((col) => {
        if (Array.isArray(col?.children) && col.children.length) {
            return {
                ...col,
                children: applyDefaultEllipsis(col.children),
            };
        }

        if (col?.render || col?.disableDefaultEllipsis) {
            return col;
        }

        return {
            ...col,
            render: (value) => renderEllipsisCell(value),
        };
    });

const paginationItemRender = (_, type, originalElement) => {
    if (type === "prev") {
        return (
            <CustomButton color="primary" variant="link">
                Prev
            </CustomButton>
        );
    }
    if (type === "next") {
        return (
            <CustomButton color="primary" variant="link">
                Next
            </CustomButton>
        );
    }
    return originalElement;
};

const CustomTable = ({
    columns,
    dataSource,
    loading = false,
    rowKey = "id",
    pagination = true,
    enableDefaultEllipsis = true,
    tableContainerStyle = '',
    tableStyle = "",
    scroll: scrollProp,
    /** Enable Columns+ preference when set with defaultColumnKeys */
    screenKey = null,
    defaultColumnKeys = null,
    onColumnControlChange = null,
    ...rest
}) => {
    const [dynamicPageSize, setDynamicPageSize] = useState(getDynamicPageSize);

    useEffect(() => {
        const handleResize = () => {
            setDynamicPageSize(getDynamicPageSize());
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const columnPreferenceEnabled = Boolean(screenKey);

    const {
        tableColumns: preferenceColumns,
        availableOptions,
        visibleOrderedKeys,
        loading: columnPrefLoading,
        saving: columnPrefSaving,
        isCustomized,
        toggleColumn,
        toggleAllColumns,
        reorderVisibleColumns,
        resetToDefault,
    } = useTableColumnPreference(
        columnPreferenceEnabled ? screenKey : null,
        columns,
        { defaultColumnKeys },
    );

    // Keep latest values in refs so parent setState is not triggered by identity churn
    // (unstable columns arrays used to cause an update loop that froze the UI / sidebar).
    const columnControlRef = useRef({
        availableOptions,
        visibleOrderedKeys,
        toggleColumn,
        toggleAllColumns,
        reorderVisibleColumns,
        resetToDefault,
        columnPrefLoading,
        columnPrefSaving,
        isCustomized,
    });
    columnControlRef.current = {
        availableOptions,
        visibleOrderedKeys,
        toggleColumn,
        toggleAllColumns,
        reorderVisibleColumns,
        resetToDefault,
        columnPrefLoading,
        columnPrefSaving,
        isCustomized,
    };

    const availableOptionsKey = availableOptions
        .map((option) => `${option.key}:${option.label}`)
        .join("|");
    const visibleOrderedKeysKey = visibleOrderedKeys.join("|");

    useEffect(() => {
        if (!columnPreferenceEnabled || !onColumnControlChange) return undefined;

        const snapshot = columnControlRef.current;
        onColumnControlChange({
            availableOptions: snapshot.availableOptions,
            visibleOrderedKeys: snapshot.visibleOrderedKeys,
            onToggleColumn: (key) =>
                columnControlRef.current.toggleColumn?.(key),
            onToggleAll: (selectAll) =>
                columnControlRef.current.toggleAllColumns?.(selectAll),
            onReorder: (fromIndex, toIndex) =>
                columnControlRef.current.reorderVisibleColumns?.(
                    fromIndex,
                    toIndex,
                ),
            onReset: () => columnControlRef.current.resetToDefault?.(),
            loading: snapshot.columnPrefLoading,
            saving: snapshot.columnPrefSaving,
            isCustomized: snapshot.isCustomized,
        });

        return () => onColumnControlChange(null);
    }, [
        columnPreferenceEnabled,
        onColumnControlChange,
        availableOptionsKey,
        visibleOrderedKeysKey,
        columnPrefLoading,
        columnPrefSaving,
        isCustomized,
    ]);

    const resolvedColumns = columnPreferenceEnabled
        ? preferenceColumns
        : columns;

    const tableColumns = useMemo(
        () =>
            enableDefaultEllipsis
                ? applyDefaultEllipsis(resolvedColumns)
                : resolvedColumns,
        [resolvedColumns, enableDefaultEllipsis],
    );

    const scrollConfig = useMemo(
        () => ({
            x: "max-content",
        }),
        [],
    );

    const paginationConfig = useMemo(() => {
        if (!pagination) {
            return false;
        }

        const basePagination = {
            position: ["bottomCenter"],
            showSizeChanger: false,
            pageSize: dynamicPageSize,
            itemRender: paginationItemRender,
        };

        if (pagination === true) {
            return basePagination;
        }

        const merged = {
            ...basePagination,
            ...pagination,
        };

        const totalItems =
            typeof merged.total === "number" && merged.total > 0
                ? merged.total
                : Array.isArray(dataSource)
                ? dataSource.length
                : 0;
        if (totalItems > 0 && merged.current && merged.pageSize) {
            const maxPage = Math.max(1, Math.ceil(totalItems / merged.pageSize));
            if (merged.current > maxPage) {
                merged.current = maxPage;
            }
        }

        return merged;
    }, [pagination, dynamicPageSize, dataSource]);

    return (
        <div className={`custom-table-container ${tableContainerStyle}`}>
            <Table
                rowKey={rowKey}
                columns={tableColumns}
                dataSource={dataSource}
                loading={loading || (columnPreferenceEnabled && columnPrefLoading)}
                scroll={scrollProp !== undefined ? scrollProp : scrollConfig}
                pagination={paginationConfig}
                className={`custom-table ${tableStyle}`}
                {...rest}
            />
        </div>
    );
};

export default memo(CustomTable);
